// Module Conformité RGPD - Interface Admin
import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { FiShield, FiFileText, FiDownload, FiTrash2, FiEye, FiClock, FiCheckCircle, FiAlertCircle, FiArchive } from 'react-icons/fi';

interface DataRequest { id: string; type: 'access' | 'deletion' | 'export'; candidateName: string; email: string; date: Date; status: 'pending' | 'completed' | 'rejected'; }
interface AuditLog { id: string; user: string; action: string; resource: string; timestamp: Date; ipAddress: string; }
interface DataProcessing { id: string; name: string; purpose: string; dataTypes: string[]; retention: string; status: 'active' | 'archived'; }

const Container = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing.xl};`;
const Header = styled.div`background: white; padding: ${theme.spacing.xl}; border-radius: ${theme.borderRadius.lg}; box-shadow: ${theme.shadows.sm};`;
const Title = styled.h1`font-size: ${theme.typography.sizes['2xl']}; font-weight: ${theme.typography.weights.bold}; color: ${theme.colors.text}; margin: 0 0 ${theme.spacing.xs} 0;`;
const Subtitle = styled.p`font-size: ${theme.typography.sizes.sm}; color: ${theme.colors.text}60; margin: 0;`;
const TabBar = styled.div`display: flex; gap: ${theme.spacing.sm}; background: white; padding: ${theme.spacing.lg}; border-radius: ${theme.borderRadius.lg}; box-shadow: ${theme.shadows.sm};`;
const Tab = styled.button<{ active?: boolean }>`padding: ${theme.spacing.md} ${theme.spacing.lg}; background: ${props => props.active ? theme.colors.primary : 'transparent'}; color: ${props => props.active ? 'white' : theme.colors.text}; border: none; border-radius: ${theme.borderRadius.md}; font-family: ${theme.typography.fontFamily}; font-size: ${theme.typography.sizes.sm}; font-weight: ${theme.typography.weights.semibold}; cursor: pointer; display: flex; align-items: center; gap: ${theme.spacing.sm}; transition: all ${theme.transitions.fast}; &:hover { background: ${props => props.active ? theme.colors.primary + 'DD' : theme.colors.surface}; }`;
const Section = styled.div`background: white; padding: ${theme.spacing.xl}; border-radius: ${theme.borderRadius.lg}; box-shadow: ${theme.shadows.sm};`;
const SectionTitle = styled.h3`font-size: ${theme.typography.sizes.xl}; font-weight: ${theme.typography.weights.semibold}; color: ${theme.colors.text}; margin: 0 0 ${theme.spacing.lg} 0; display: flex; align-items: center; gap: ${theme.spacing.sm};`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`padding: ${theme.spacing.md}; text-align: left; font-size: ${theme.typography.sizes.xs}; font-weight: ${theme.typography.weights.semibold}; color: ${theme.colors.text}80; text-transform: uppercase; border-bottom: 2px solid ${theme.colors.surface};`;
const Td = styled.td`padding: ${theme.spacing.lg}; font-size: ${theme.typography.sizes.sm}; color: ${theme.colors.text}; border-bottom: 1px solid ${theme.colors.surface};`;
const Badge = styled.div<{ color?: string }>`display: inline-flex; align-items: center; gap: ${theme.spacing.xs}; padding: ${theme.spacing.xs} ${theme.spacing.sm}; background: ${props => props.color || theme.colors.primary}20; color: ${props => props.color || theme.colors.primary}; border-radius: ${theme.borderRadius.full}; font-size: ${theme.typography.sizes.xs}; font-weight: ${theme.typography.weights.semibold}; text-transform: uppercase;`;
const Actions = styled.div`display: flex; gap: ${theme.spacing.xs};`;
const IconButton = styled.button`padding: ${theme.spacing.sm}; background: transparent; border: none; border-radius: ${theme.borderRadius.sm}; cursor: pointer; color: ${theme.colors.text}60; transition: all ${theme.transitions.fast}; display: flex; align-items: center; justify-content: center; &:hover { background: ${theme.colors.surface}; color: ${theme.colors.primary}; }`;
const StatCard = styled.div`padding: ${theme.spacing.lg}; background: ${theme.colors.surface}50; border-radius: ${theme.borderRadius.md}; display: flex; justify-content: space-between; align-items: center; margin-bottom: ${theme.spacing.md};`;
const StatLabel = styled.div`font-size: ${theme.typography.sizes.sm}; color: ${theme.colors.text}80;`;
const StatValue = styled.div`font-size: ${theme.typography.sizes['2xl']}; font-weight: ${theme.typography.weights.bold}; color: ${theme.colors.text};`;

export const Compliance: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'rgpd' | 'audit' | 'registry'>('rgpd');

  const dataRequests: DataRequest[] = [
    { id: '1', type: 'access', candidateName: 'Marie Dubois', email: 'marie.d@example.com', date: new Date(), status: 'pending' },
    { id: '2', type: 'deletion', candidateName: 'Jean Martin', email: 'jean.m@example.com', date: new Date(Date.now() - 86400000), status: 'completed' },
    { id: '3', type: 'export', candidateName: 'Sophie Bernard', email: 'sophie.b@example.com', date: new Date(Date.now() - 172800000), status: 'completed' }
  ];

  const auditLogs: AuditLog[] = [
    { id: '1', user: 'sophie.admin@org.fr', action: 'LOGIN', resource: 'System', timestamp: new Date(), ipAddress: '192.168.1.10' },
    { id: '2', user: 'marie.manager@org.fr', action: 'UPDATE_SCORING', resource: 'Candidat #8934', timestamp: new Date(Date.now() - 3600000), ipAddress: '192.168.1.15' },
    { id: '3', user: 'jean.agent@org.fr', action: 'VERIFY_DOCUMENT', resource: 'Document #1234', timestamp: new Date(Date.now() - 7200000), ipAddress: '192.168.1.20' }
  ];

  const dataProcessings: DataProcessing[] = [
    { id: '1', name: 'Traitement Candidatures', purpose: 'Attribution logements sociaux', dataTypes: ['Identité', 'Revenus', 'Situation familiale'], retention: '5 ans', status: 'active' },
    { id: '2', name: 'Scoring Automatique', purpose: 'Évaluation éligibilité', dataTypes: ['Données financières', 'Documents'], retention: '3 ans', status: 'active' },
    { id: '3', name: 'Historique Sessions Commission', purpose: 'Traçabilité décisions', dataTypes: ['Votes anonymes', 'PV'], retention: '10 ans', status: 'active' }
  ];

  const getRequestTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = { access: 'Droit d\'accès', deletion: 'Droit à l\'oubli', export: 'Export données' };
    return labels[type] || type;
  };

  const stats = {
    totalRequests: dataRequests.length,
    pendingRequests: dataRequests.filter(r => r.status === 'pending').length,
    totalProcessings: dataProcessings.length,
    totalAuditLogs: auditLogs.length
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <Title>Conformité & RGPD</Title>
          <Subtitle>Gestion des données personnelles et traçabilité</Subtitle>
        </Header>

        <TabBar>
          <Tab active={activeTab === 'rgpd'} onClick={() => setActiveTab('rgpd')}><FiShield /> RGPD</Tab>
          <Tab active={activeTab === 'audit'} onClick={() => setActiveTab('audit')}><FiClock /> Audit</Tab>
          <Tab active={activeTab === 'registry'} onClick={() => setActiveTab('registry')}><FiFileText /> Registre</Tab>
        </TabBar>

        {activeTab === 'rgpd' && (
          <Section>
            <SectionTitle><FiShield /> Demandes RGPD</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: theme.spacing.md, marginBottom: theme.spacing.xl }}>
              <StatCard><StatLabel>Total demandes</StatLabel><StatValue>{stats.totalRequests}</StatValue></StatCard>
              <StatCard><StatLabel>En attente</StatLabel><StatValue style={{ color: theme.colors.warning }}>{stats.pendingRequests}</StatValue></StatCard>
              <StatCard><StatLabel>Délai moyen</StatLabel><StatValue>2.3j</StatValue></StatCard>
            </div>
            <Table>
              <thead>
                <tr><Th>Candidat</Th><Th>Type</Th><Th>Date</Th><Th>Statut</Th><Th>Actions</Th></tr>
              </thead>
              <tbody>
                {dataRequests.map(req => (
                  <tr key={req.id}>
                    <Td><div style={{ fontWeight: theme.typography.weights.semibold }}>{req.candidateName}</div><div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '60' }}>{req.email}</div></Td>
                    <Td><Badge color={theme.colors.info}>{getRequestTypeLabel(req.type)}</Badge></Td>
                    <Td>{req.date.toLocaleDateString('fr-FR')}</Td>
                    <Td><Badge color={req.status === 'completed' ? theme.colors.success : req.status === 'pending' ? theme.colors.warning : theme.colors.error}>{req.status === 'completed' ? <FiCheckCircle size={12} /> : <FiClock size={12} />} {req.status === 'completed' ? 'Complété' : req.status === 'pending' ? 'En attente' : 'Rejeté'}</Badge></Td>
                    <Td><Actions><IconButton title="Voir"><FiEye /></IconButton><IconButton title="Exporter"><FiDownload /></IconButton>{req.type === 'deletion' && <IconButton title="Supprimer"><FiTrash2 /></IconButton>}</Actions></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        )}

        {activeTab === 'audit' && (
          <Section>
            <SectionTitle><FiClock /> Logs d'Audit</SectionTitle>
            <Table>
              <thead>
                <tr><Th>Utilisateur</Th><Th>Action</Th><Th>Ressource</Th><Th>Date/Heure</Th><Th>IP</Th></tr>
              </thead>
              <tbody>
                {auditLogs.map(log => (
                  <tr key={log.id}>
                    <Td>{log.user}</Td>
                    <Td><Badge color={theme.colors.primary}>{log.action}</Badge></Td>
                    <Td>{log.resource}</Td>
                    <Td>{log.timestamp.toLocaleString('fr-FR')}</Td>
                    <Td style={{ fontFamily: 'Courier New, monospace', fontSize: theme.typography.sizes.xs }}>{log.ipAddress}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        )}

        {activeTab === 'registry' && (
          <Section>
            <SectionTitle><FiFileText /> Registre des Traitements</SectionTitle>
            <Table>
              <thead>
                <tr><Th>Traitement</Th><Th>Finalité</Th><Th>Données</Th><Th>Conservation</Th><Th>Statut</Th></tr>
              </thead>
              <tbody>
                {dataProcessings.map(proc => (
                  <tr key={proc.id}>
                    <Td style={{ fontWeight: theme.typography.weights.semibold }}>{proc.name}</Td>
                    <Td>{proc.purpose}</Td>
                    <Td><div style={{ fontSize: theme.typography.sizes.xs }}>{proc.dataTypes.join(', ')}</div></Td>
                    <Td><Badge color={theme.colors.warning}><FiClock size={12} /> {proc.retention}</Badge></Td>
                    <Td><Badge color={proc.status === 'active' ? theme.colors.success : theme.colors.text}>{proc.status === 'active' ? <FiCheckCircle size={12} /> : <FiArchive size={12} />} {proc.status === 'active' ? 'Actif' : 'Archivé'}</Badge></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        )}
      </Container>
    </DashboardLayout>
  );
};
